import { filesApi } from '@api/files';
import i18n from '@/i18n';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import type { MediaUploadValue } from './MediaUpload';

export function fmtBytes(n?: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// The BE is fail-closed: `POST /files/upload` returns `publicUrl: null` and a
// background MediaPipelineJob (30s interval + ~20s startup lag) only fills the
// URL once ClamAV reports Clean/Skipped. 45 polls × 2s = 90s covers two
// pipeline ticks plus slack before we give up.
const SCAN_POLL_INTERVAL_MS = 2000;
const SCAN_POLL_MAX_ATTEMPTS = 45;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll `GET /files/{id}` until the async ClamAV scan produces a servable URL.
 * Resolves with the `publicUrl`, or throws a user-facing error when the scan
 * terminally fails (Infected/Failed) or never clears within the deadline.
 */
async function waitForScannedUrl(
  id: string,
  onProgress?: (n: number) => void,
): Promise<string> {
  for (let attempt = 0; attempt < SCAN_POLL_MAX_ATTEMPTS; attempt += 1) {
    await delay(SCAN_POLL_INTERVAL_MS);
    const file = await filesApi.get(id);
    if (file.scanStatus === 'Infected' || file.scanStatus === 'Failed') {
      throw new Error(i18n.t('components:mediaUpload.scanFailed'));
    }
    if (file.publicUrl) {
      return file.publicUrl;
    }
    // Creep the bar through the reserved 90..99 scan band without hitting 100.
    onProgress?.(Math.min(90 + attempt + 1, 99));
  }
  throw new Error(i18n.t('components:mediaUpload.scanTimeout'));
}

/**
 * Real BE uploader — POSTs to /api/v1/admin/files/upload, then waits for the
 * server-issued publicUrl. The upload response is NOT final: it carries a URL
 * only when the file dedups onto an already-scanned blob, otherwise it is null
 * until the background media pipeline clears the ClamAV scan. So we poll the
 * detail endpoint and only resolve once a servable URL exists — consumers that
 * persist the URL (RichTextEditor image, Brand.LogoUrl, OgImage, …) never
 * write an empty `src`. Throws when the scan fails or times out.
 *
 * Pass this to <MediaUpload uploader={filesUploader}/>. The default mockUpload
 * emits a base64 data URL that fits the 500-char DB column for exactly zero
 * real images, so we never want it in production.
 */
export async function filesUploader(
  file: File,
  onProgress?: (n: number) => void,
): Promise<MediaUploadValue> {
  // Reserve the top of the bar (90..100) for the scan wait: the transfer's
  // 0..100 maps onto 0..90 so the user still sees motion during scanning.
  const result = await filesApi.upload(file, null, (p) =>
    onProgress?.(Math.round(p * 0.9)),
  );

  const base = {
    id: result.id,
    name: file.name,
    size: file.size,
    mime: file.type,
  };

  // BE relative `/uploads/...` dönebilir (PublicBaseUrl set edilmemiş ortamlar +
  // dedup'un DB'den döndürdüğü ESKİ kayıtlar). Admin FE, API'den farklı origin'de
  // servis edildiğinde relative src kırık görsel demek — her yolda API origin'ine çöz.
  const url = resolveAssetUrl(
    result.publicUrl ?? (await waitForScannedUrl(result.id, onProgress)),
  );
  onProgress?.(100);
  return { ...base, url };
}

export async function mockUpload(
  file: File,
  onProgress?: (n: number) => void,
): Promise<MediaUploadValue> {
  return new Promise((resolve) => {
    let p = 0;
    const t = setInterval(() => {
      p += 18;
      onProgress?.(Math.min(p, 95));
      if (p >= 95) {
        clearInterval(t);
        const reader = new FileReader();
        reader.onloadend = () => {
          onProgress?.(100);
          const result = typeof reader.result === 'string' ? reader.result : '';
          resolve({
            url: result,
            name: file.name,
            size: file.size,
            mime: file.type,
          });
        };
        reader.readAsDataURL(file);
      }
    }, 120);
  });
}
